import { withDependencies } from '@wix/thunderbolt-ioc'
import { IViewerManagerImplementor, RootNavigationInfo, ViewerAPI } from 'thunderbolt-viewer-manager-types'
import { IEditorNavigation, EditorNavigationSymbol, INavigationInfo } from 'ds-feature-editor-navigation'
import { ScrollRestorationAPISymbol, IScrollRestorationAPI } from 'feature-scroll-restoration'
import {
	NavigationInfoSymbol,
	DSViewModeAPISymbol,
	IViewMode,
	PagesModelsRegistrarSymbol,
	IPagesModelsRegistrar,
} from '@wix/thunderbolt-symbols'
import { RuntimeApiHandlers } from './types'
import { name, RuntimeApiSymbol } from './symbols'
import { IModelUpdatesHandlersImplementor } from 'ds-feature-model-updates-invoker'
import {
	IPageTransition,
	IPageTransitionsCompleted,
	PageTransitionsCompletedSymbol,
	PageTransitionsSymbol,
} from 'feature-page-transitions'
import { ScreenInAPISymbol, IScreenInAPI } from 'feature-screen-in'
import { CustomPageContextSymbol, ICustomPageContext } from 'feature-router'

type NavigationParams = {
	shouldTriggerSoapScreenInAnimation: boolean
}

const viewerManagerRuntimeApiFactory = (
	navigation: IEditorNavigation,
	{
		getNextRootNavigationInfo,
		getCurrentRootNavigationInfo,
		getPrimaryPageNavigationInfo,
		getPopUpNavigationInfo,
	}: INavigationInfo,
	viewMode: IViewMode,
	scrollRestorationAPI: IScrollRestorationAPI,
	screenInAPI: IScreenInAPI,
	pageTransition: IPageTransition,
	pageTransitionsCompleted: IPageTransitionsCompleted,
	pagesModelsRegistrar: IPagesModelsRegistrar,
	customPageContext: ICustomPageContext
): ViewerAPI['runtime'] & IModelUpdatesHandlersImplementor<RuntimeApiHandlers> => {
	let prevPreviewEditorMode: string
	let prevViewMode: string
	const setWantedNavInfo = (navInfo: Partial<RootNavigationInfo>) => {
		const navigationPromise = navigation.navigateTo(navInfo)
		navigation.setNextNavigationIntent(navigationPromise)
	}

	const triggerNavigation = ({ shouldTriggerSoapScreenInAnimation }: NavigationParams) => {
		const popupNavigationInfo = getPopUpNavigationInfo()
		const primaryPageNavigationInfo = getPrimaryPageNavigationInfo()
		pageTransition.disableNextTransition()
		scrollRestorationAPI.updateHistoryStateCurrentScrollY()

		const navigationPromise = navigation
			.navigateTo(primaryPageNavigationInfo, { isSameContext: false })
			.then(async () => {
				if (popupNavigationInfo) {
					pageTransition.disableNextTransition()
					await navigation.navigateTo(popupNavigationInfo, { isSameContext: false })
				}

				// when triggering edit/preview - mobile/desktop navigations we disable page transitions but
				// we need also to release all listeners of page transition complete to trigger appDidLoadPage lifecycle
				pageTransitionsCompleted.notifyPageTransitionsCompleted(primaryPageNavigationInfo.pageId!)
			})

		if (shouldTriggerSoapScreenInAnimation) {
			navigationPromise.then(() => screenInAPI.triggerSoapScreenInAnimation())
		}

		navigation.setNextNavigationIntent(navigationPromise)
	}

	const setWantedIsMobileView = (shouldSwitchToMobileView: boolean) => {
		const viewModeSwitchPromise = viewMode.changeViewMode(shouldSwitchToMobileView ? 'mobile' : 'desktop')
		viewMode.setNextViewModeSwitchIntent(viewModeSwitchPromise)
	}

	return {
		refreshPage: () => {
			customPageContext.generateNewContext()
			triggerNavigation({ shouldTriggerSoapScreenInAnimation: prevViewMode === 'preview' })
		},
		setPagesModelsFetcher: pagesModelsRegistrar.setPagesModelsFetcher,
		getWantedNavInfo: () => getNextRootNavigationInfo() || getCurrentRootNavigationInfo(),
		setWantedNavInfo,
		getWantedIsMobileView: viewMode.getWantedIsMobileView,
		setWantedIsMobileView,
		featureName: name,
		handlers: {
			onPreviewEditorModeChanged: ({ previewEditorMode: newPreviewEditorMode }) => {
				if (prevPreviewEditorMode && prevPreviewEditorMode !== newPreviewEditorMode) {
					const shouldTriggerSoapScreenInAnimation = newPreviewEditorMode === 'preview'
					triggerNavigation({ shouldTriggerSoapScreenInAnimation })
				}
				prevPreviewEditorMode = newPreviewEditorMode
			},
			onViewModeChanged: ({ viewMode: newViewMode }) => {
				if (prevViewMode && prevViewMode !== newViewMode) {
					const shouldTriggerSoapScreenInAnimation =
						prevPreviewEditorMode === 'preview' && newViewMode === 'desktop'
					triggerNavigation({ shouldTriggerSoapScreenInAnimation })
				}
				prevViewMode = newViewMode
			},
		},
	}
}

export const ViewerManagerRuntimeApi = withDependencies(
	[RuntimeApiSymbol],
	(runtimeApi: ViewerAPI['runtime']): IViewerManagerImplementor<'runtime'> => ({
		runtime: {
			refreshPage: runtimeApi.refreshPage,
			setPagesModelsFetcher: runtimeApi.setPagesModelsFetcher,
			getWantedNavInfo: runtimeApi.getWantedNavInfo,
			setWantedNavInfo: runtimeApi.setWantedNavInfo,
			getWantedIsMobileView: runtimeApi.getWantedIsMobileView,
			setWantedIsMobileView: runtimeApi.setWantedIsMobileView,
		},
	})
)

export const RuntimeApi = withDependencies(
	[
		EditorNavigationSymbol,
		NavigationInfoSymbol,
		DSViewModeAPISymbol,
		ScrollRestorationAPISymbol,
		ScreenInAPISymbol,
		PageTransitionsSymbol,
		PageTransitionsCompletedSymbol,
		PagesModelsRegistrarSymbol,
		CustomPageContextSymbol,
	],
	viewerManagerRuntimeApiFactory
)
